<template>
  <div class="pub-sign">
    <!-- Loading -->
    <div class="pub-sign__loading" v-if="loading">
      <div class="pub-sign__spinner">
        <i class="el-icon-loading" />
      </div>
      <p>Cargando documento...</p>
    </div>

    <!-- Error state -->
    <div class="pub-sign__error" v-else-if="error">
      <div class="pub-sign__error-icon">
        <i class="el-icon-warning-outline" />
      </div>
      <p class="pub-sign__error-title">{{ error.title }}</p>
      <p class="pub-sign__error-text">{{ error.message }}</p>
    </div>

    <!-- Already signed -->
    <div class="pub-sign__done" v-else-if="alreadySigned">
      <div class="pub-sign__done-icon">
        <i class="el-icon-circle-check" />
      </div>
      <p class="pub-sign__done-title">Documento firmado</p>
      <p class="pub-sign__done-text">
        Ya has firmado este documento. Recibirás una copia por email cuando
        todos los firmantes hayan completado el proceso.
      </p>
    </div>

    <!-- Declined -->
    <div class="pub-sign__done" v-else-if="declined">
      <div class="pub-sign__done-icon pub-sign__done-icon--declined">
        <i class="el-icon-circle-close" />
      </div>
      <p class="pub-sign__done-title">Has rechazado firmar</p>
      <p class="pub-sign__done-text">
        Se ha notificado al emisor del documento.
      </p>
    </div>

    <!-- Main signing view -->
    <template v-else-if="document">
      <!-- Top bar -->
      <div class="pub-sign__topbar">
        <div class="pub-sign__topbar-left">
          <p class="pub-sign__topbar-title">{{ document.name }}</p>
          <span class="pub-sign__topbar-from" v-if="document.business_name">
            De: {{ document.business_name }}
          </span>
        </div>
        <div class="pub-sign__topbar-right">
          <button
            class="btn md__btn-third-secondary"
            @click="showDeclineDialog = true"
          >
            Rechazar
          </button>
          <button class="btn md__btn-primary" @click="openSignModal">
            <i class="el-icon-edit-outline" /> Firmar
          </button>
        </div>
      </div>

      <!-- Document viewer -->
      <div class="pub-sign__viewer">
        <div class="pub-sign__viewer-inner">
          <!-- PDF pages rendered from arraybuffer -->
          <div
            class="pub-sign__page"
            v-for="(page, idx) in pdfPages"
            :key="idx"
          >
            <canvas :ref="'pdfCanvas' + idx" />
          </div>

          <!-- HTML content fallback -->
          <div
            class="pub-sign__html-content"
            v-if="!pdfPages.length && document.content_html"
            v-html="document.content_html"
          />

          <!-- No content -->
          <div
            class="pub-sign__no-content"
            v-if="!pdfPages.length && !document.content_html"
          >
            <i class="el-icon-document" />
            <p>El documento no tiene vista previa disponible.</p>
          </div>
        </div>
      </div>

      <!-- Signer info bar -->
      <div class="pub-sign__footer">
        <div class="pub-sign__footer-info">
          <span class="pub-sign__footer-label">Firmante:</span>
          {{ signer.name }} ({{ signer.email }})
        </div>
        <div class="pub-sign__footer-actions">
          <button class="btn md__btn-primary" @click="openSignModal">
            <i class="el-icon-edit-outline" /> Firmar documento
          </button>
        </div>
      </div>

      <!-- Sign modal -->
      <SignConfirmModal
        :show.sync="showSignModal"
        :signer="signer"
        :document-name="document.name"
        :submitting="submitting"
        @confirm="submitSignature"
        @cancel="showSignModal = false"
      />

      <!-- Decline dialog -->
      <el-dialog
        :visible.sync="showDeclineDialog"
        title="Rechazar firma"
        width="440px"
        :close-on-click-modal="false"
      >
        <p style="font-size: 13px; color: #989aae; margin: 0 0 14px">
          Si rechazas, se notificará al emisor. Opcionalmente puedes agregar un
          motivo.
        </p>
        <el-input
          type="textarea"
          v-model="declineReason"
          :rows="3"
          placeholder="Motivo del rechazo (opcional)"
        />
        <span slot="footer">
          <button
            class="btn md__btn-third-secondary mr-2"
            @click="showDeclineDialog = false"
          >
            Cancelar
          </button>
          <button
            class="btn md__btn-primary"
            style="background: #e24b4a; border-color: #e24b4a"
            :disabled="submitting"
            @click="declineDocument"
          >
            <i class="el-icon-loading" v-if="submitting" />
            Rechazar firma
          </button>
        </span>
      </el-dialog>
    </template>
  </div>
</template>

<script>
import documentoService from "../services/documentoService";
import SignConfirmModal from "../components/SignConfirmModal";

export default {
  name: "PublicSigningPage",
  components: { SignConfirmModal },
  data() {
    return {
      loading: true,
      error: null,
      document: null,
      signer: {},
      pdfPages: [],
      pdfLib: null,
      alreadySigned: false,
      declined: false,
      showSignModal: false,
      showDeclineDialog: false,
      declineReason: "",
      submitting: false
    };
  },
  computed: {
    token() {
      return this.$route.params.token;
    }
  },
  methods: {
    async loadDocument() {
      try {
        const response = await documentoService.getSigningDocument(this.token);
        const { success, data } = response.data;

        if (!success) {
          this.error = {
            title: "Enlace inválido",
            message:
              data.message || "Este enlace de firma no es válido o ha expirado."
          };
          return;
        }

        this.document = data.document;
        this.signer = data.signer || {};

        if (this.signer.status === "signed") {
          this.alreadySigned = true;
          return;
        }
        if (this.signer.status === "declined") {
          this.declined = true;
          return;
        }

        // Try to load PDF
        this.loadPdf();
      } catch (e) {
        const status = e.response ? e.response.status : null;
        if (status === 404 || status === 410) {
          this.error = {
            title: "Documento no encontrado",
            message: "Este enlace de firma ha expirado o no existe."
          };
        } else {
          this.error = {
            title: "Error",
            message: "No se pudo cargar el documento. Intenta nuevamente."
          };
        }
      } finally {
        this.loading = false;
      }
    },

    loadPdfScript() {
      return new Promise((resolve, reject) => {
        if (window.pdfjsLib) return resolve(window.pdfjsLib);
        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
        script.onload = () => {
          const lib = window.pdfjsLib;
          lib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
          resolve(lib);
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    async loadPdf() {
      try {
        const response = await documentoService.getSigningPdf(this.token);
        if (!response.data || !response.data.byteLength) return;

        const pdfjsLib = await this.loadPdfScript();
        const typedArray = new Uint8Array(response.data);
        const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;

        this.pdfPages = Array.from({ length: pdf.numPages }, (_, i) => i + 1);

        await this.$nextTick();

        for (let i = 0; i < pdf.numPages; i++) {
          const page = await pdf.getPage(i + 1);
          const scale = 1.5;
          const viewport = page.getViewport({ scale });
          const canvasRef = this.$refs["pdfCanvas" + i];
          if (!canvasRef || !canvasRef[0]) continue;
          const canvas = canvasRef[0];
          const ctx = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: ctx, viewport }).promise;
        }
      } catch (e) {
        // PDF load failed — fallback to HTML content
        this.pdfPages = [];
      }
    },

    openSignModal() {
      this.showSignModal = true;
    },

    async submitSignature(payload) {
      this.submitting = true;
      try {
        const response = await documentoService.submitSignature(
          this.token,
          payload
        );
        const { success } = response.data;
        if (success) {
          this.alreadySigned = true;
          this.showSignModal = false;
        } else {
          this.$message.error(
            response.data.message || "Error al firmar el documento"
          );
        }
      } catch (e) {
        this.$message.error("Error al procesar la firma. Intenta nuevamente.");
      } finally {
        this.submitting = false;
      }
    },

    async declineDocument() {
      this.submitting = true;
      try {
        const response = await documentoService.declineSignature(this.token, {
          reason: this.declineReason || null
        });
        const { success } = response.data;
        if (success) {
          this.declined = true;
          this.showDeclineDialog = false;
        } else {
          this.$message.error(response.data.message || "Error al rechazar");
        }
      } catch (e) {
        this.$message.error("Error al procesar. Intenta nuevamente.");
      } finally {
        this.submitting = false;
      }
    }
  },
  mounted() {
    this.loadDocument();
  }
};
</script>

<style lang="scss" scoped>
.pub-sign {
  min-height: 100vh;
  background: #f7f8f9;
  display: flex;
  flex-direction: column;
}

/* ─── Loading ────────────────────────────────────────────────────── */
.pub-sign__loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  .pub-sign__spinner {
    font-size: 32px;
    color: #3fdbbf;
    margin-bottom: 12px;
  }
  p {
    font-size: 14px;
    color: #989aae;
    margin: 0;
  }
}

/* ─── Error / Done states ────────────────────────────────────────── */
.pub-sign__error,
.pub-sign__done {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}
.pub-sign__error-icon {
  font-size: 48px;
  color: #e24b4a;
  margin-bottom: 16px;
}
.pub-sign__error-title,
.pub-sign__done-title {
  font-size: 20px;
  font-weight: 600;
  color: #31355c;
  margin: 0 0 8px;
}
.pub-sign__error-text,
.pub-sign__done-text {
  font-size: 14px;
  color: #989aae;
  margin: 0;
  max-width: 420px;
}
.pub-sign__done-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #e1f5ee;
  color: #0f6e56;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin-bottom: 16px;
  &--declined {
    background: #fcebeb;
    color: #a32d2d;
  }
}

/* ─── Top bar ────────────────────────────────────────────────────── */
.pub-sign__topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: #fff;
  border-bottom: 1px solid #e4eaf6;
  flex-shrink: 0;
}
.pub-sign__topbar-left {
  min-width: 0;
}
.pub-sign__topbar-title {
  font-size: 15px;
  font-weight: 600;
  color: #31355c;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pub-sign__topbar-from {
  font-size: 12px;
  color: #989aae;
}
.pub-sign__topbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-left: 16px;
}

/* ─── Viewer ─────────────────────────────────────────────────────── */
.pub-sign__viewer {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  justify-content: center;
}
.pub-sign__viewer-inner {
  width: 100%;
  max-width: 800px;
}
.pub-sign__page {
  background: #fff;
  border: 1px solid #e4eaf6;
  border-radius: 8px;
  margin-bottom: 16px;
  box-shadow: 0 0 10px 0 #d6e4f3;
  overflow: hidden;
  canvas {
    display: block;
    width: 100%;
    height: auto;
  }
}
.pub-sign__html-content {
  background: #fff;
  border: 1px solid #e4eaf6;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 0 10px 0 #d6e4f3;
  font-size: 14px;
  color: #31355c;
  line-height: 1.7;
}
.pub-sign__no-content {
  text-align: center;
  padding: 60px 20px;
  i {
    font-size: 40px;
    color: #989aae;
    margin-bottom: 12px;
  }
  p {
    font-size: 14px;
    color: #989aae;
    margin: 0;
  }
}

/* ─── Footer bar ─────────────────────────────────────────────────── */
.pub-sign__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: #fff;
  border-top: 1px solid #e4eaf6;
  flex-shrink: 0;
}
.pub-sign__footer-info {
  font-size: 13px;
  color: #31355c;
}
.pub-sign__footer-label {
  color: #989aae;
  margin-right: 4px;
}
.pub-sign__footer-actions {
  flex-shrink: 0;
  margin-left: 16px;
}

/* ─── Decline dialog ─────────────────────────────────────────────── */
::v-deep .el-textarea__inner {
  border: 1.5px solid #e4eaf6;
  border-radius: 10px;
  background: #fafbfd;
  font-size: 13px;
  &:focus {
    border-color: #3fdbbf;
    background: #fff;
  }
}

/* ─── Responsive ─────────────────────────────────────────────────── */
@media (max-width: 640px) {
  .pub-sign__topbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  .pub-sign__topbar-right {
    margin-left: 0;
    width: 100%;
    justify-content: flex-end;
  }
  .pub-sign__footer {
    flex-direction: column;
    gap: 10px;
    text-align: center;
  }
  .pub-sign__footer-actions {
    margin-left: 0;
  }
  .pub-sign__viewer {
    padding: 16px;
  }
  .pub-sign__html-content {
    padding: 20px;
  }
}
</style>
